import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap, catchError } from 'rxjs/operators';
import { PieChartComponent } from '../../../shared/reusables/pie-chart/pie-chart.component';
import { BarChartComponent } from '../../../shared/reusables/bar-chart/bar-chart.component';
import { AlertComponent } from '../../../shared/reusables/alert/alert.component';
import { ToastService } from '../../../shared/services/toast.service';
import { AuthService } from '../../../shared/services';
import { TooltipComponent } from '../../../shared/tooltip/tooltip.component';
import { ExcelService } from '../../../shared/services/excel.service';
import * as bootstrap from 'bootstrap';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { isProduct, isSellable } from '../../../../shared/models/item-type';
import { InventoryDataService, ItemTypeDTO } from '../../../../shared/services/inventory-data.service';
import { InventoryImportService, ImportContext } from '../../../../shared/services/inventory-import.service';
import { ProductionSaleService } from '../../../../shared/services/production-sale.service';
import { InventoryItemModalComponent } from '../inventory-item-modal/inventory-item-modal.component';
import { MobileInventoryViewComponent } from '../mobile-inventory-view/mobile-inventory-view.component';
import {
  CategoryDTO,
  SupplierDTO,
  UnitOfMeasureDTO,
  EnhancedItemDTO,
  ItemDTO,
  InventoryTransactionDTO,
} from '../inventory-dashboard.component';

interface BOMComponents {
  bomComponentID: number;
  bomHeaderID: number;
  rawMaterialItemID: number;
  quantityRequired: number;
  componentUOMID: number;
  scrapFactor: number;
  notes: string;
}
interface BOMHeaders {
  bomHeaderID: number;
  finishedGoodItemID: number;
  bomVersion: string;
  description: string;
  isActive: boolean;
  validFrom: string;
  validTo: string;
  createdAt: string;
  userIDCreated: number;
  finishedGoodItem: any;
  createdByUser: any;
  components?: BOMComponents[];
  expanded?: boolean;
}

type FinishedSortableKeys = keyof Pick<EnhancedItemDTO, 'itemCode' | 'itemName' | 'purchasePrice' | 'sellingPrice' | 'purchaseUOMID' | 'CategoryName'>;

/**
 * Products page — split out of InventoryDashboardComponent (W5 step 4/5).
 * Covers Finished Good and Service item types (both product-like forms in
 * the shared add/edit modal). Owns only the products half of the old
 * monolith's state: its own finishedGoods/filteredFinishedGoods, its own
 * search/pagination/sort, its own filter-panel state (an independent
 * selectedCategoryFilter — the old component shared one between both
 * panels, fixed simply by these being separate classes now), its own
 * add/edit modal state, and its own charts/statistics computed only from
 * its Finished Good + Service item set.
 *
 * Still fetches via InventoryDataService.loadItems() (the full item list,
 * filtered client-side to products in ngOnInit) — a later pass swaps this
 * for a products-only fetch.
 */
@Component({
  selector: 'app-inventory-products',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PieChartComponent,
    BarChartComponent,
    AlertComponent,
    TooltipComponent,
    InventoryItemModalComponent,
    MobileInventoryViewComponent,
  ],
  templateUrl: './inventory-products.component.html',
  styleUrls: [
    '../../shared/offcanvas-sheet/offcanvas-sheet.shared.css',
    '../inventory-dashboard.component.css',
  ],
})
export class InventoryProductsComponent implements OnInit {
  readonly String = String;

  // Data
  finishedGoods: EnhancedItemDTO[] = [];
  originalFinishedGoods: EnhancedItemDTO[] = [];
  filteredFinishedGoods: EnhancedItemDTO[] = [];
  categories: CategoryDTO[] = [];
  suppliers: SupplierDTO[] = [];
  uomList: UnitOfMeasureDTO[] = [];
  itemTypes: ItemTypeDTO[] = [];

  // Stats
  totalFinishedGoods = 0;
  totalProductValueFromBOM = 0;

  // Charts
  categoryLabels: string[] = [];
  categoryData: number[] = [];
  // All distinct category names present in the table, for badge colouring —
  // kept separate from categoryLabels because that one is filtered to
  // categories with quantity > 0 for the stock-value chart, and most
  // finished goods/services carry zero on-hand quantity.
  allCategoryNames: string[] = [];
  stackedBarData: {
    labels: string[];
    datasets: { label: string; data: number[]; backgroundColor: string[]; borderRadius: number; borderWidth: number }[];
  } | null = null;
  baseColors = [
    '#007A8F', '#6C7A3E', '#B0213C', '#26284F', '#7FBF7F',
    '#F3D250', '#2C6B47', '#A0D83A', '#3C9C8E', '#FF6F61', '#FF2E17',
  ];
  ItemsPerPage = 10;
  currentPage = 1;
  totalPages = 1;
  totalItemsCount = 0;
  goToPage = (page: number) => this.onPageChange(page);
  onPageSizeChange = (size: number) => {
    this.ItemsPerPage = Number(size) > 0 ? Number(size) : 10;
    this.currentPage = 1;
    this.updateChartData(1);
  };

  // Sort / pagination (table)
  finishedSortColumn: FinishedSortableKeys = 'itemCode';
  finishedSortDirection: 'asc' | 'desc' = 'desc';
  finishedGoodsCurrentPage = 1;
  finishedGoodsPageSize = 15;

  // Search / filters — independent of the Raw Materials page's filter state
  finishedGoodsSearchTerm = '';
  selectedCategoryFilter: number | null = null;
  NoProductStock = false;
  WithProductStock = false;
  productTypeFilter: 'all' | 'physical' | 'service' = 'all';
  isFilterPanelOpenFinishedGoods = false;

  // Add/edit modal state
  isEditing = false;
  isFinishedGood = false;
  isServiceItem = false;
  isAddingQuantity = false;
  isUpdatingPurchasePrice = false;
  showPurchaseFields = false;
  showAddModal = false;
  showDeleteModal = false;
  animateClickFinished = false;
  selectedItem: EnhancedItemDTO | null = null;
  selectedSupplier: SupplierDTO | null = null;
  selectedCategory: CategoryDTO | null = null;
  isCreatingSupplier = false;
  isCreatingCategory = false;

  // Sell modal state
  showSellModal = false;
  sellItem: EnhancedItemDTO | null = null;
  sellQuantity = 1;
  sellAvailable = 0;
  isLoadingSellAvailability = false;
  isSelling = false;

  supplierForm: FormGroup;
  inventoryForm: FormGroup;
  categoryForm: FormGroup;

  // Alert
  errorMessage: string | null = null;
  isAlertVisible = false;
  alertTitle = '';
  alertMessage = '';
  alertBgColor = 'bg-danger';
  alerttextcolor = '#ffffff';

  userRole: string | null = null;
  tenantName: any;
  fullName: any;
  isLoading = true;

  // Import/Export
  isImporting = false;
  importProgress = 0;
  importResults: { success: number; errors: any[] } = { success: 0, errors: [] };
  @ViewChild('fileInput') fileInput!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    public authService: AuthService,
    private toast: ToastService,
    private excelService: ExcelService,
    private inventoryDataService: InventoryDataService,
    private inventoryImportService: InventoryImportService,
    private productionSaleService: ProductionSaleService,
    private router: Router,
  ) {
    this.supplierForm = this.fb.group({
      companyName: ['', Validators.required],
      primaryContactPerson: ['', Validators.required],
      primaryPhone: ['', Validators.required],
      primaryEmail: ['', [Validators.required, Validators.email]],
      website: [''],
      branchName: ['', Validators.required],
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      city: ['', Validators.required],
      region: [''],
      postalCode: ['', Validators.required],
      country: ['', Validators.required],
      locationContactPerson: ['', Validators.required],
      locationEmail: ['', [Validators.required, Validators.email]],
      locationPhone: ['', Validators.required],
    });

    this.inventoryForm = this.fb.group({
      itemName: ['', Validators.required],
      itemCode: [''],
      PurchaseUOMID: [null, [Validators.required]],
      quantityToAdd: [0, [Validators.required, Validators.min(0)]],
      currentQuantity: [0],
      lowStockThreshold: [null, [Validators.required, Validators.min(0)]],
      purchasePrice: [0, [Validators.required, Validators.min(0)]],
      unitCost: [0],
      profitMarginPercent: [0, [Validators.min(0), Validators.max(100)]],
      sellingPrice: [0, [Validators.min(0)]],
      valuationMethodCode: ['FIFO'],
      categoryID: [null, Validators.required],
      itemTypeID: [2, Validators.required],
      supplierID: [null],
      isActive: [true],
    });

    this.categoryForm = this.fb.group({
      categoryName: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    const token = this.authService.getToken && this.authService.getToken();
    if (!token) {
      this.authService.logOut();
      return;
    }
    const decodedToken: any = jwtDecode(token);
    this.tenantName = decodedToken.TenantName;
    this.fullName = decodedToken.FullName;
    this.userRole = this.authService.getUserRole();
    this.loadUnitsOfMeasure();
    this.loadItemTypes();

    this.loadCategories().subscribe();
    this.loadSuppliers().subscribe({
      next: () => this.loadItems(),
      error: () => {
        this.showAlert('Error', 'Failed to load initial data');
      },
    });
  }

  private loadUnitsOfMeasure(): void {
    this.inventoryDataService.loadUnitsOfMeasure().subscribe({
      next: (uoms) => { this.uomList = uoms; },
      error: () => {},
    });
  }

  loadCategories(): Observable<CategoryDTO[]> {
    return this.inventoryDataService.loadCategories().pipe(tap((categories) => { this.categories = categories; }));
  }

  loadSuppliers(): Observable<SupplierDTO[]> {
    return this.inventoryDataService.loadSuppliers().pipe(tap((suppliers) => { this.suppliers = suppliers; }));
  }

  loadItemTypes(): void {
    this.inventoryDataService.loadItemTypes().pipe(tap((itemTypes) => { this.itemTypes = itemTypes; })).subscribe();
  }

  get desktopSupplierOptions() {
    return this.suppliers.map(s => ({ value: String(s.supplierID), label: `${s.companyName}${s.primaryEmail ? ' - ' + s.primaryEmail : ''}` }));
  }

  get desktopCategoryOptions() {
    return this.categories.map(c => ({ value: String(c.categoryId), label: c.categoryName }));
  }

  loadItems(): void {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    this.inventoryDataService.loadItemsByTypes([2, 4]).subscribe({
      next: async ({ items, transactions }) => {
        const categoryMap = new Map<number, string>();
        this.categories.forEach(category => categoryMap.set(category.categoryId, category.categoryName));

        const transactionsByItem = new Map<number, InventoryTransactionDTO[]>();
        transactions.forEach(tx => {
          if (!transactionsByItem.has(tx.itemID)) transactionsByItem.set(tx.itemID, []);
          transactionsByItem.get(tx.itemID)?.push(tx);
        });

        // GET /api/Items/ByType/{2,4} already filters to Finished Good + Service and isActive on the server.
        const productItems = items;

        const itemsWithQuantities = productItems.map(item => {
          const itemTransactions = transactionsByItem.get(item.itemID) || [];
          const latestTransaction = itemTransactions
            .filter(tx => tx.costPerUnit !== null && tx.costPerUnit !== undefined)
            .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime())[0];
          const latestUnitCost = latestTransaction ? latestTransaction.costPerUnit : (item.unitCost || item.purchasePrice || 0);

          return {
            ...item,
            quantity: this.calculateItemQuantity(item.itemID, transactions),
            unitCost: latestUnitCost,
            CategoryName: categoryMap.get(item.categoryID) || 'Unknown',
          };
        });

        this.finishedGoods = itemsWithQuantities;
        this.originalFinishedGoods = [...itemsWithQuantities];
        this.filteredFinishedGoods = [...itemsWithQuantities];

        this.totalProductValueFromBOM = 0;
        const allItemsForBOM = [...this.finishedGoods];
        const [bomHeaders, allBomComponents] = await Promise.all([
          this.http.get<BOMHeaders[]>(`${environment.apiUrl}/api/BOMHeaders/GetAll`, { headers }).toPromise(),
          this.http.get<BOMComponents[]>(`${environment.apiUrl}/api/BOMComponents/GetAll`, { headers }).toPromise(),
        ]);

        for (const product of this.finishedGoods) {
          const productBOM = (bomHeaders || []).find(bom => bom.finishedGoodItemID === product.itemID);
          if (!productBOM) continue;

          const bomComponents = (allBomComponents || []).filter(comp => comp.bomHeaderID === productBOM.bomHeaderID);
          if (bomComponents.length === 0) continue;

          if (product.isActive) {
            const bomCost = bomComponents.reduce((bomTotal, comp) => {
              const componentItem = allItemsForBOM.find(item => item.itemID === comp.rawMaterialItemID)
                ?? (items as any[]).find(i => i.itemID === comp.rawMaterialItemID);
              if (!componentItem) return bomTotal;
              const componentUnitCost = (componentItem.purchasePrice / componentItem.purchaseQuantity) || 0;
              return bomTotal + (comp.quantityRequired * componentUnitCost);
            }, 0);
            this.totalProductValueFromBOM += (product.quantity ?? 0) * bomCost;
          }
        }

        this.calculateStatistics();
        this.prepareChartsData();
        this.updateChartData(1);
        this.isLoading = false;
      },
      error: () => {
        this.showAlert('Error', 'Failed to load inventory data');
      },
    });
  }

  calculateItemQuantity(itemID: number, transactions: InventoryTransactionDTO[]): number {
    const itemTransactions = transactions.filter(transaction => transaction.itemID === itemID);
    return itemTransactions.reduce((sum, transaction) => sum + transaction.quantityChange, 0);
  }

  calculateStatistics(): void {
    this.totalFinishedGoods = this.finishedGoods.length;
  }

  // ── Charts (products only — no more All/Raw/Finished 3-way toggle) ──────
  prepareChartsData(): void {
    const categoryMap = new Map<string, number>();
    const categoryQuantityMap = new Map<string, number>();
    const filteredItems = this.finishedGoods.filter(item => item.isActive);

    if (filteredItems.length === 0) {
      this.categoryLabels = [];
      this.categoryData = [];
      this.allCategoryNames = [];
      this.stackedBarData = null;
      return;
    }

    filteredItems.forEach(item => {
      const categoryName = item.CategoryName || 'Uncategorized';
      const itemQuantity = item.quantity || item.stockLevel || 0;
      const itemValue = itemQuantity * (item.purchasePrice / item.purchaseQuantity || 0);
      categoryMap.set(categoryName, (categoryMap.get(categoryName) || 0) + itemValue);
      categoryQuantityMap.set(categoryName, (categoryQuantityMap.get(categoryName) || 0) + itemQuantity);
    });

    this.allCategoryNames = Array.from(new Set(filteredItems.map(item => item.CategoryName || 'Uncategorized')));

    const sortedCategoryEntries = Array.from(categoryMap.entries()).filter(([, value]) => value > 0).sort((a, b) => b[1] - a[1]);
    const categoryLabels = sortedCategoryEntries.map(([key]) => key);
    const categoryValues = sortedCategoryEntries.map(([, value]) => value);

    const sortedQuantityEntries = Array.from(categoryQuantityMap.entries()).filter(([, value]) => value > 0).sort((a, b) => b[1] - a[1]);
    this.categoryLabels = sortedQuantityEntries.map(([key]) => key);
    this.categoryData = sortedQuantityEntries.map(([, value]) => value);

    this.stackedBarData = {
      labels: categoryLabels,
      datasets: [{ label: 'Stock Value', data: categoryValues, backgroundColor: this.baseColors.slice(0, categoryLabels.length), borderRadius: 4, borderWidth: 0 }],
    };
  }

  onPageChange(newPage: number): void {
    this.currentPage = newPage;
    this.updateChartData(newPage);
  }

  updateChartData(page: number = 1): void {
    const filteredItems = this.finishedGoods;

    if (!filteredItems || filteredItems.length === 0) {
      this.totalItemsCount = 0;
      this.totalPages = 1;
      this.currentPage = 1;
      this.stackedBarData = { labels: [], datasets: [{ label: 'Stock Value', data: [], backgroundColor: [], borderRadius: 4, borderWidth: 0 }] };
      return;
    }

    const categoryMap = new Map<string, number>();
    filteredItems.forEach(item => {
      const categoryName = item.CategoryName || 'Uncategorized';
      const itemQuantity = item.quantity || item.stockLevel || 0;
      const itemValue = itemQuantity * (item.purchasePrice / item.purchaseQuantity || 0);
      categoryMap.set(categoryName, (categoryMap.get(categoryName) || 0) + itemValue);
    });

    const sortedEntries = Array.from(categoryMap.entries()).filter(([, value]) => value > 0).sort((a, b) => b[1] - a[1]);
    const allLabels = sortedEntries.map(([key]) => key);
    const allValues = sortedEntries.map(([, value]) => value);

    this.totalItemsCount = allLabels.length;
    const itemsPerPage = Math.max(1, this.ItemsPerPage || 10);
    this.totalPages = Math.max(1, Math.ceil(allLabels.length / itemsPerPage));
    this.currentPage = Math.max(1, Math.min(page, this.totalPages));

    this.stackedBarData = {
      labels: allLabels,
      datasets: [{ label: 'Stock Value', data: allValues, backgroundColor: this.baseColors.slice(0, allLabels.length), borderRadius: 4, borderWidth: 0 }],
    };
  }

  getCategoryColor(categoryName: string): string {
    const index = this.allCategoryNames.findIndex(label => label === categoryName);
    if (index !== -1 && index < this.baseColors.length) {
      return this.hexToRgba(this.baseColors[index], 0.7);
    }
    return 'rgba(204, 204, 204, 0.5)';
  }

  hexToRgba(hex: string, alpha: number): string {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  getUOMName(uomId: number): string {
    if (!uomId) return '';
    const uom = this.uomList.find(u => u.uomid === uomId);
    return uom ? uom.uomCode.replace(/2$/, '²') : '';
  }

  getSelectedUomCode(): string {
    const id = this.inventoryForm.get('PurchaseUOMID')?.value;
    if (!id) return '';
    return this.uomList.find(u => u.uomid === id)?.uomCode ?? '';
  }

  setQuantityToAdd(val: number): void {
    this.inventoryForm.get('quantityToAdd')?.setValue(val);
  }

  compareUOM(uom1: any, uom2: any): boolean {
    const id1 = typeof uom1 === 'object' ? uom1?.uomid : uom1;
    const id2 = typeof uom2 === 'object' ? uom2?.uomid : uom2;
    return id1 === id2;
  }

  sortFinishedBy(column: FinishedSortableKeys): void {
    if (this.finishedSortColumn === column) {
      this.finishedSortDirection = this.finishedSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.finishedSortColumn = column;
      this.finishedSortDirection = 'asc';
    }

    const sorted = [...this.filteredFinishedGoods].sort((a, b) => {
      const valueA: any = (a as any)[column] ?? '';
      const valueB: any = (b as any)[column] ?? '';

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return this.finishedSortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (typeof valueA === 'number' && typeof valueB === 'number') {
        return this.finishedSortDirection === 'asc' ? valueA - valueB : valueB - valueA;
      } else {
        if (valueA < valueB) return this.finishedSortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return this.finishedSortDirection === 'asc' ? 1 : -1;
        return 0;
      }
    });

    this.filteredFinishedGoods = sorted;
  }

  filterFinishedGoods(): void {
    const searchTerm = this.finishedGoodsSearchTerm.toLowerCase().trim();

    this.filteredFinishedGoods = this.originalFinishedGoods.filter(item => {
      const matchesSearch = !searchTerm ||
        item.itemName?.toLowerCase().includes(searchTerm) ||
        item.itemCode?.toLowerCase().includes(searchTerm) ||
        this.getUOMName(item.purchaseUOMID)?.toLowerCase().includes(searchTerm) ||
        item.CategoryName?.toLowerCase().includes(searchTerm);

      const matchesCategory = !this.selectedCategoryFilter || item.categoryID === this.selectedCategoryFilter;

      const qty = item.quantity ?? 0;
      const noProductStock = !this.NoProductStock || (qty === 0);
      const withProductStock = !this.WithProductStock || (qty !== 0);

      const matchesType = this.productTypeFilter === 'all' ||
        (this.productTypeFilter === 'physical' && item.itemTypeID === 2) ||
        (this.productTypeFilter === 'service' && item.itemTypeID === 4);

      return matchesSearch && matchesCategory && noProductStock && withProductStock && matchesType;
    });

    this.finishedGoodsCurrentPage = 1;
  }

  toggleFilterPanelFinishedGoods(): void {
    this.isFilterPanelOpenFinishedGoods = !this.isFilterPanelOpenFinishedGoods;
  }

  get finishedGoodsPaged(): EnhancedItemDTO[] {
    const start = (this.finishedGoodsCurrentPage - 1) * this.finishedGoodsPageSize;
    const end = start + this.finishedGoodsPageSize;
    return this.filteredFinishedGoods.slice(start, end);
  }

  get finishedGoodsTotalPages(): number {
    const length = this.filteredFinishedGoods ? this.filteredFinishedGoods.length : 0;
    return Math.max(1, Math.ceil(length / this.finishedGoodsPageSize) || 1);
  }

  get finishedGoodsPages(): number[] {
    const total = this.finishedGoodsTotalPages;
    const maxVisible = 7;
    if (total <= maxVisible) return Array.from({ length: total }, (_, index) => index + 1);

    const half = Math.floor(maxVisible / 2);
    let start = this.finishedGoodsCurrentPage - half;
    let end = this.finishedGoodsCurrentPage + half;
    if (start < 1) { start = 1; end = maxVisible; }
    else if (end > total) { end = total; start = total - maxVisible + 1; }

    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  }

  changeFinishedGoodsPage(page: number): void {
    const total = this.finishedGoodsTotalPages;
    if (page < 1 || page > total) return;
    this.finishedGoodsCurrentPage = page;
  }

  // ── Add/Edit modal ──────────────────────────────────────────────────────
  handleFinishedClick(): void {
    this.resetFormStates();
    this.isFinishedGood = true;
    this.isServiceItem = false;
    this.animateClickFinished = true;

    this.inventoryForm.reset({
      itemName: '',
      description: '',
      purchasePrice: 0,
      sellingPrice: 0,
      profitMarginPercent: 0,
      quantityToAdd: 0,
      currentQuantity: 0,
      lowStockThreshold: 0,
      valuationMethodCode: 'FIFO',
      itemCode: '',
      PurchaseUOMID: null,
      categoryID: null,
      itemTypeID: 2,
      isActive: true,
      supplierID: 1,
    });

    this.showAddModal = true;
    setTimeout(() => { this.animateClickFinished = false; }, 300);
  }

  handleServiceClick(): void {
    this.resetFormStates();
    this.isFinishedGood = true;
    this.isServiceItem = true;
    this.animateClickFinished = true;

    this.inventoryForm.reset({
      itemName: '',
      description: '',
      purchasePrice: 0,
      sellingPrice: 0,
      profitMarginPercent: 0,
      // For a Service this is the time it takes, in minutes.
      quantityToAdd: 1,
      currentQuantity: 0,
      lowStockThreshold: 0,
      valuationMethodCode: 'FIFO',
      itemCode: '',
      PurchaseUOMID: null,
      categoryID: null,
      itemTypeID: 4,
      isActive: true,
      supplierID: null,
    });

    this.showAddModal = true;
    setTimeout(() => { this.animateClickFinished = false; }, 300);
  }

  editItem(item: EnhancedItemDTO): void {
    this.isEditing = true;
    this.selectedItem = item;
    this.isFinishedGood = isProduct(item);
    this.isServiceItem = item.itemTypeID === 4;
    this.isUpdatingPurchasePrice = false;
    this.showPurchaseFields = false;
    this.showAddModal = true;

    const itemCode = item.itemCode || this.generateSKU(item.itemName);

    this.inventoryForm.reset();
    const formValues: any = {
      itemTypeID: item.itemTypeID,
      itemName: item.itemName,
      itemCode: itemCode,
      sellingPrice: item.sellingPrice || 0,
      PurchaseUOMID: item.purchaseUOMID,
      // For a Service this field is the delivery time in minutes, held in
      // PurchaseQuantity — load it back so editing shows the real value.
      quantityToAdd: item.itemTypeID === 4 ? (item.purchaseQuantity || 1) : 0,
      currentQuantity: item.quantity || 0,
      lowStockThreshold: item.lowStockThreshold || 0,
      purchasePrice: item.purchasePrice || 0,
      profitMarginPercent: item.profitMarginPercent || 0,
      valuationMethodCode: item.valuationMethodCode || 'FIFO',
      categoryID: item.categoryID,
      supplierID: item.supplierID || null,
      isActive: item.isActive !== false,
    };
    formValues.unitCost = item.unitCost !== undefined ? item.unitCost : item.purchasePrice || 0;
    this.inventoryForm.patchValue(formValues);

    this.selectedCategory = this.categories.find(c => c.categoryId === item.categoryID) || null;
    if (item.supplierID) {
      this.selectedSupplier = this.suppliers.find(s => s.supplierID === item.supplierID) || null;
    }

    this.inventoryForm.markAsUntouched();
    this.inventoryForm.markAsPristine();
  }

  private resetFormStates(): void {
    this.isEditing = false;
    this.selectedItem = null;
    this.selectedSupplier = null;
    this.selectedCategory = null;
    this.isAddingQuantity = false;
    this.isUpdatingPurchasePrice = false;
    this.showPurchaseFields = false;
    this.isCreatingSupplier = false;
    this.isCreatingCategory = false;
    this.isServiceItem = false;

    this.inventoryForm.reset();
    this.supplierForm.reset();
    this.categoryForm.reset();

    this.inventoryForm.patchValue({
      quantityToAdd: 1,
      currentQuantity: 0,
      purchasePrice: 0,
      sellingPrice: 0,
      profitMarginPercent: 0,
      isActive: true,
      itemTypeID: 2,
      valuationMethodCode: 'FIFO',
    });

    this.inventoryForm.markAsUntouched();
    this.inventoryForm.markAsPristine();
    this.supplierForm.markAsUntouched();
    this.supplierForm.markAsPristine();
    this.categoryForm.markAsUntouched();
    this.categoryForm.markAsPristine();
  }

  closeAddModal(): void {
    if (!this.isEditing) {
      this.resetFormStates();
      this.inventoryForm.reset();
    }
    this.showAddModal = false;
    if (!this.isEditing) {
      this.selectedItem = null;
    }
  }

  togglePurchaseFields(): void {
    this.showPurchaseFields = !this.showPurchaseFields;
    if (this.showPurchaseFields && this.selectedItem) {
      this.inventoryForm.patchValue({
        quantityToAdd: this.selectedItem.quantity || 0,
        purchasePrice: this.selectedItem.purchasePrice || 0,
      });
    } else if (!this.showPurchaseFields) {
      this.inventoryForm.patchValue({ purchasePrice: null, quantityToAdd: null });
    }
  }

  onDesktopSupplierSelected(idStr: string | null): void {
    this.selectedSupplier = this.suppliers.find(s => String(s.supplierID) === idStr) ?? null;
  }

  onDesktopCategorySelected(idStr: string | null): void {
    const cat = this.categories.find(c => String(c.categoryId) === idStr) ?? null;
    this.onCategorySelected(cat);
  }

  onCategorySelected(category: CategoryDTO | null): void {
    this.selectedCategory = category;
    if (category) {
      this.inventoryForm.patchValue({ categoryID: category.categoryId });
      this.inventoryForm.get('categoryID')?.markAsTouched();
      this.inventoryForm.get('categoryID')?.markAsDirty();
    } else {
      this.inventoryForm.patchValue({ categoryID: null });
    }
  }

  createSupplier(): void {
    if (this.supplierForm.invalid) {
      Object.keys(this.supplierForm.controls).forEach(field => {
        this.supplierForm.get(field)?.markAsTouched({ onlySelf: true });
      });
      return;
    }

    const newSupplier: SupplierDTO = {
      supplierID: 0,
      companyName: this.supplierForm.value.companyName,
      primaryContactPerson: this.supplierForm.value.primaryContactPerson,
      primaryEmail: this.supplierForm.value.primaryEmail,
      primaryPhone: this.supplierForm.value.primaryPhone,
      website: this.supplierForm.value.website,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    const newLocation: any = {
      branchName: this.supplierForm.value.branchName,
      addressLine1: this.supplierForm.value.addressLine1,
      addressLine2: this.supplierForm.value.addressLine2,
      city: this.supplierForm.value.city,
      region: this.supplierForm.value.region,
      postalCode: this.supplierForm.value.postalCode,
      country: this.supplierForm.value.country,
      locationContactPerson: this.supplierForm.value.locationContactPerson,
      locationEmail: this.supplierForm.value.locationEmail,
      locationPhone: this.supplierForm.value.locationPhone,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.inventoryDataService.createSupplier(newSupplier, newLocation).subscribe({
      next: (response) => {
        const supplierWithLocation = { ...response.supplier, locations: [response.location] };
        this.selectedSupplier = supplierWithLocation;
        this.isCreatingSupplier = false;
        this.supplierForm.reset();
        this.suppliers.push(supplierWithLocation);
        this.toast.success('Supplier and location created successfully', 3000);
      },
      error: () => {
        this.toast.error('Failed to create supplier or location. Please try again.');
      },
    });
  }

  createCategory(): void {
    if (this.categoryForm.invalid) return;
    const newCategory: CategoryDTO = { categoryId: 0, categoryName: this.categoryForm.value.categoryName, createdAt: new Date() };
    this.inventoryDataService.createCategory(newCategory).subscribe({
      next: (response) => {
        this.selectedCategory = response;
        this.onCategorySelected(response);
        this.isCreatingCategory = false;
        this.categoryForm.reset();
        this.categories.push(response);
      },
      error: (err) => console.error('Error creating category:', err),
    });
  }

  generateSKU(productName: string): string {
    return this.inventoryDataService.generateSKU(productName);
  }

  navigateToAddSupplier(): void {
    // Products page has no supplier section in the shared modal (isFinishedGood is always true here),
    // but the modal still emits this output for parity — no-op on this page.
  }

  saveItem(): void {
    const formValue = this.inventoryForm.getRawValue();

    if (!formValue.categoryID) {
      this.showAlert('Validation Error', 'Please select a category');
      return;
    }
    if (!formValue.PurchaseUOMID) {
      this.showAlert('Validation Error', 'Please select a unit of measure');
      return;
    }

    let itemTypeId = 2;
    if (this.isServiceItem) {
      itemTypeId = 4;
    } else if (this.isFinishedGood) {
      itemTypeId = 2;
    }

    const itemData: any = {
      ItemID: this.isEditing && this.selectedItem ? this.selectedItem.itemID : 0,
      ItemName: formValue.itemName,
      ItemCode: formValue.itemCode || this.generateSKU(formValue.itemName),
      itemTypeID: itemTypeId,
      PurchaseUOMID: formValue.PurchaseUOMID,
      lowStockThreshold: formValue.lowStockThreshold ?? 0,
      isActive: this.isEditing && this.selectedItem ? (this.selectedItem.isActive ?? true) : true,
      valuationMethodCode: formValue.valuationMethodCode || 'FIFO',
      categoryID: formValue.categoryID,
      profitMarginPercent: formValue.profitMarginPercent || 0,
      sellingPrice: formValue.sellingPrice || 0,
      supplierID: this.isServiceItem ? null : 1,
    };

    let hasActualChanges = false;
    if (!this.isEditing) {
      itemData.purchasePrice = 0;
      itemData.unitCost = 0;
      // A Service is quantified as time: PurchaseQuantity holds the minutes it
      // takes to deliver. It still holds no stock, so no movement is written.
      itemData.purchaseQuantity = this.isServiceItem
        ? (formValue.quantityToAdd || 1)
        : (formValue.quantityToAdd || 1);
      itemData.quantity = this.isServiceItem ? 0 : (formValue.quantityToAdd || 0);
      hasActualChanges = !this.isServiceItem;
    } else if (this.selectedItem) {
      itemData.purchasePrice = this.selectedItem.purchasePrice || 0;
      itemData.unitCost = this.selectedItem.unitCost || 0;
      // Editing a Service persists the newly entered minutes.
      itemData.purchaseQuantity = this.isServiceItem
        ? (formValue.quantityToAdd || this.selectedItem.purchaseQuantity || 1)
        : (this.selectedItem.purchaseQuantity || 1);
      itemData.quantity = this.selectedItem.quantity || 0;
    }

    (itemData as any).hasActualChanges = hasActualChanges;
    itemData.sellingPrice = formValue.sellingPrice || 0;
    itemData.purchasePrice = 0;

    const apiUrl = this.isEditing && this.selectedItem
      ? `${environment.apiUrl}/api/Items/Update?ItemId=${this.selectedItem.itemID}`
      : `${environment.apiUrl}/api/Items/Add`;
    const httpMethod = this.isEditing ? 'put' : 'post';
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });

    this.http.request<ItemDTO>(httpMethod, apiUrl, { body: itemData, headers, observe: 'response', responseType: 'json' }).pipe(
      switchMap((response: any) => {
        if (response.status >= 400) throw new Error(`Server returned status ${response.status}`);
        const itemResponse = response.body;
        if (!itemResponse) throw new Error('No response body received from server');
        const savedItemId = this.isEditing && this.selectedItem ? this.selectedItem.itemID : itemResponse.itemID;

        if ((itemData as any).hasActualChanges) {
          const inventoryTransaction: InventoryTransactionDTO = {
            itemID: savedItemId,
            warehouseID: 1,
            transactionType: this.isEditing ? 'Adjustment' : 'Receipt',
            quantityChange: formValue.quantityToAdd || 0,
            costPerUnit: formValue.unitCost || 0,
            lowStockThreshold: formValue.lowStockThreshold !== undefined ? formValue.lowStockThreshold : 0,
            transactionDate: new Date(),
            referenceType: 'Manual Entry',
            referenceID: `UI-${new Date().getTime()}`,
            userID: 1,
            notes: `${this.isEditing ? 'Updated' : 'Added'} via Inventory Dashboard`,
            batchNumber: null,
            expiryDate: null,
          };
          const txHeaders = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('authToken')}`);
          return this.http.post(`${environment.apiUrl}/api/InventoryTransactions/Add`, inventoryTransaction, { headers: txHeaders }).pipe(
            map(() => ({ itemId: savedItemId }))
          );
        }
        return of({ itemId: savedItemId });
      })
    ).subscribe({
      next: () => {
        this.loadItems();
        this.showAddModal = false;
        this.inventoryForm.reset();
        this.showAlert('Success', `Item ${this.isEditing ? 'updated' : 'added'} successfully`, '#4de270');
      },
      error: (error) => {
        if (error.error) {
          try {
            const errorObj = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;
            this.errorMessage = errorObj.title || 'Failed to save item. Please try again.';
            if (errorObj.errors) {
              this.errorMessage = Object.entries(errorObj.errors)
                .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                .join('\n') || this.errorMessage;
            }
          } catch {
            this.errorMessage = 'Failed to save item. Please try again.';
          }
        } else {
          this.errorMessage = 'Failed to save item. Please try again.';
        }
        this.showAlert('Error', this.errorMessage || 'Failed to save item. Please try again.', 'bg-danger');
      },
    });
  }

  deleteItem(item: EnhancedItemDTO): void {
    const token = localStorage.getItem('authToken');
    if (!token) {
      this.toast.error('Session expired. Please log in again.', 3000);
      setTimeout(() => { this.authService.logOut(); }, 3000);
      return;
    }

    const headers = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });
    const updatedItem = { ...item, isActive: false };

    this.http.put(`${environment.apiUrl}/api/Items/Update?ItemId=${item.itemID}`, updatedItem, { headers }).pipe(
      catchError(error => {
        let msg = 'Failed to remove item. Please try again.';
        if (error.status === 404) msg = 'Item not found.';
        else if (error.status === 401) msg = 'Unauthorized. Please log in again.';
        else if (error.status === 409) msg = 'Cannot remove item as it is referenced by other records.';
        this.toast.error(msg, 3000);
        throw error;
      })
    ).subscribe({
      next: () => {
        this.finishedGoods = this.finishedGoods.filter(fg => fg.itemID !== item.itemID);
        this.filteredFinishedGoods = this.filteredFinishedGoods.filter(fg => fg.itemID !== item.itemID);
        this.calculateStatistics();
        this.prepareChartsData();
        this.showAddModal = false;
        this.selectedItem = null;
        this.selectedSupplier = null;
        this.selectedCategory = null;
        this.inventoryForm.reset();
        this.showAlert('Success', `Item ${item.itemName} deactivated successfully`, 'bg-success');
      },
    });

    this.loadItems();
  }

  // ── Sell ──────────────────────────────────────────────────────────────────
  canSellItem(item: EnhancedItemDTO): boolean {
    return isSellable(item);
  }

  openSell(item: EnhancedItemDTO): void {
    this.sellItem = item;
    this.sellQuantity = 1;
    this.sellAvailable = 0;
    this.showSellModal = true;
    this.isLoadingSellAvailability = true;

    this.productionSaleService.getAvailableToSell(item.itemID).subscribe({
      next: (available) => {
        // Production orders can say more is "remaining" than is actually on
        // hand (e.g. stock adjusted/wasted outside the order) — never offer
        // to sell more than the item's real stock quantity.
        this.sellAvailable = Math.min(available, item.quantity ?? available);
        this.isLoadingSellAvailability = false;
      },
      error: () => {
        this.isLoadingSellAvailability = false;
        this.showAlert('Error', 'Failed to load available stock for this product.', 'bg-danger');
      },
    });
  }

  closeSellModal(): void {
    this.showSellModal = false;
    this.sellItem = null;
  }

  get sellShortfall(): number {
    return Math.max(0, this.sellQuantity - this.sellAvailable);
  }

  confirmSell(): void {
    if (!this.sellItem || this.sellQuantity <= 0) return;
    const item = this.sellItem;
    const qty = Math.min(this.sellQuantity, this.sellAvailable);
    if (qty <= 0) return;

    this.isSelling = true;
    this.productionSaleService.sellQuantity(item.itemID, qty, { isService: item.itemTypeID === 4 }).subscribe({
      next: () => {
        this.loadItems();
        this.showAlert('Success', `Sold ${qty} unit(s) of ${item.itemName}.`, 'bg-success');

        const remaining = this.sellQuantity - qty;
        if (remaining <= 0) {
          this.isSelling = false;
          this.closeSellModal();
          return;
        }

        // Still short of what was asked for — refresh the popup instead of
        // closing it blind, so it reflects the sale (available now lower,
        // sellShortfall recalculated) and offers Manufacture for the rest.
        this.sellQuantity = remaining;
        this.productionSaleService.getAvailableToSell(item.itemID).subscribe({
          next: (available) => {
            this.sellAvailable = Math.min(available, item.quantity ?? available);
            this.isSelling = false;
          },
          error: () => {
            this.isSelling = false;
          },
        });
      },
      error: () => {
        this.isSelling = false;
        this.showAlert('Error', 'Failed to sell item. Please try again.', 'bg-danger');
      },
    });
  }

  goManufacture(): void {
    if (!this.sellItem) return;
    const shortfall = this.sellShortfall;
    this.router.navigate(['/manufacturing'], {
      queryParams: { productId: this.sellItem.itemID, qty: shortfall, action: 'manufacture' },
    });
    this.closeSellModal();
  }

  showAlert(title: string, message: string, bgColor: string = 'bg-danger', textColor: string = 'text-white'): void {
    this.alertTitle = title;
    this.alertMessage = message;
    this.alertBgColor = bgColor;
    this.alerttextcolor = textColor;
    this.isAlertVisible = true;
    setTimeout(() => { this.isAlertVisible = false; }, 5000);
  }

  // ── Import / Export ──────────────────────────────────────────────────────
  async downloadFinishedGoodTemplate(): Promise<void> {
    try {
      const categories = this.categories.map(c => c.categoryName);
      const uoms = this.uomList.map(u => u.uomCode);
      const itemTypeNames = this.itemTypes.map(t => t.typeName).filter(t => t !== 'Raw Material');
      await this.excelService.generateProductsTemplate(categories, uoms, undefined, itemTypeNames);
    } catch {
      this.showAlert('Error', 'Failed to generate template. Please try again.', 'bg-danger');
    }
  }

  errorCount(): number {
    return this.inventoryImportService.errorCount(this.importResults?.errors || []);
  }

  warningCount(): number {
    return this.inventoryImportService.warningCount(this.importResults?.errors || []);
  }

  hasFinishedGoodErrors(): boolean {
    return this.inventoryImportService.hasFinishedGoodErrors(this.importResults?.errors || [], this.itemTypes);
  }

  async downloadFailedFinishedGoods(): Promise<void> {
    try {
      const { exported } = await this.inventoryImportService.downloadFailedFinishedGoods(
        this.importResults?.errors || [],
        { categories: this.categories, uomList: this.uomList, itemTypes: this.itemTypes }
      );
      if (!exported) {
        this.showAlert('No Finished Goods', 'There are no failed finished goods rows to export.', 'bg-warning');
      }
    } catch {
      this.showAlert('Error', 'Failed to export failed rows. Please try again.', 'bg-danger');
    }
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    this.processImportFile(file);
  }

  private buildImportContext(): ImportContext {
    const existingItems: any[] = [...(this.finishedGoods || []), ...(this.originalFinishedGoods || [])];
    return {
      categories: this.categories,
      suppliers: this.suppliers,
      uomList: this.uomList,
      itemTypes: this.itemTypes,
      existingItems,
    };
  }

  async processImportFile(file: File): Promise<void> {
    this.isImporting = true;
    this.importProgress = 0;
    this.importResults = { success: 0, errors: [] };

    try {
      const context = this.buildImportContext();
      const result = await this.inventoryImportService.processImportFile(file, context, (progress) => { this.importProgress = progress; });
      this.importResults.success = result.success;
      this.importResults.errors = result.errors;

      if (!result.validItemsFound) {
        setTimeout(() => {
          this.isImporting = false;
          const errorModalElement = document.getElementById('errorDetailsModalProducts');
          if (errorModalElement) new bootstrap.Modal(errorModalElement).show();
        }, 300);
        return;
      }

      this.showAlert('Success', 'Imported ' + this.importResults.success + ' items successfully', 'bg-success');
      setTimeout(() => {
        this.isImporting = false;
        if (this.importResults.errors.length > 0) {
          const errorModalElement = document.getElementById('errorDetailsModalProducts');
          if (errorModalElement) new bootstrap.Modal(errorModalElement).show();
        }
        this.loadItems();
      }, 500);
    } catch {
      this.isImporting = false;
      this.showAlert('Error', 'Failed to process import file. Please check the format and try again.', 'bg-danger');
    } finally {
      if (this.fileInput) {
        this.fileInput.nativeElement.value = '';
      }
    }
  }

  // ── Mobile ────────────────────────────────────────────────────────────────
  // The mobile layout is the same feature as the desktop one, so it is hosted
  // by this page too (spec §1 splits the screen, not just its desktop view).
  // These handlers back the <app-mobile-inventory-view> outputs.

  saveItemMobile(payload: any): void {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!token) { this.toast.error('Session expired. Please log in again.', 3000); return; }
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });

    if (payload.isAddingQuantity && payload.selectedItem) {
      const item = payload.selectedItem;
      const tx = {
        ItemID: item.itemID,
        WarehouseID: 1,
        TransactionType: 'Receipt',
        QuantityChange: Number(payload.itemData.quantityToAdd ?? 1),
        CostPerUnit: item.purchaseQuantity > 0 ? item.purchasePrice / item.purchaseQuantity : item.purchasePrice,
        TransactionDate: new Date().toISOString(),
        ReferenceType: 'Manual',
        ReferenceID: `ADD-${Date.now()}`,
        UserID: 1,
        Notes: 'Quantity added via Mobile',
        BatchNumber: null,
        ExpiryDate: null,
        ReferenceTypeCode: 'MANUAL',
      };
      this.http.post(`${environment.apiUrl}/api/InventoryTransactions/Add`, tx, { headers }).subscribe({
        next: () => { this.loadItems(); this.toast.success('Quantity added successfully', 3000); },
        error: () => this.toast.error('Failed to add quantity. Please try again.', 3000),
      });
      return;
    }

    const { itemData, isEditing, mode, itemID } = payload;
    // Spec §2: a Service is its own product type — no quantity, no supplier,
    // and quantity defaults to 1 on the backend so nothing else has to change.
    const isService = mode === 'service';
    const stored = isEditing && itemID ? this.originalFinishedGoods.find(i => i.itemID === itemID) : undefined;

    const body: any = {
      ItemID: isEditing && itemID ? itemID : 0,
      ItemName: itemData.itemName,
      ItemCode: itemData.itemCode || this.generateSKU(itemData.itemName),
      itemTypeID: isService ? 4 : 2,
      PurchaseUOMID: itemData.purchaseUOMID,
      lowStockThreshold: itemData.lowStockThreshold ?? 0,
      isActive: itemData.isActive ?? true,
      valuationMethodCode: 'FIFO',
      categoryID: itemData.categoryID,
      // The phone form has no markup field; the markup is set in the Cost Calculator.
      profitMarginPercent: stored ? stored.profitMarginPercent : 0,
      // Spec §3: selling price is optional and falls back to R0.
      sellingPrice: itemData.sellingPrice ?? 0,
      purchasePrice: 0,
      purchaseQuantity: 1,
      quantity: isService ? 0 : (itemData.quantity ?? 0),
      unitCost: 0,
      supplierID: isService ? null : 1,
    };

    const url = isEditing && itemID
      ? `${environment.apiUrl}/api/Items/Update?ItemId=${itemID}`
      : `${environment.apiUrl}/api/Items/Add`;
    const method = isEditing ? 'put' : 'post';

    this.http.request<any>(method, url, { body, headers, observe: 'response' }).pipe(
      switchMap((resp: any) => {
        if (resp.status >= 400) throw new Error(`Server error ${resp.status}`);
        const savedId = isEditing && itemID ? itemID : resp.body?.itemID;
        if (!savedId) return of(null);
        // A Service carries no stock, so it never writes an inventory movement.
        if (isService || isEditing) return of(savedId);
        const tx: InventoryTransactionDTO = {
          itemID: savedId,
          warehouseID: 1,
          transactionType: 'Receipt',
          quantityChange: itemData.quantity ?? 0,
          costPerUnit: 0,
          lowStockThreshold: itemData.lowStockThreshold ?? 0,
          transactionDate: new Date(),
          referenceType: 'Manual Entry',
          referenceID: `UI-${Date.now()}`,
          userID: 1,
          notes: 'Added via Mobile',
          batchNumber: null,
          expiryDate: null,
        };
        return this.http.post(`${environment.apiUrl}/api/InventoryTransactions/Add`, tx, { headers }).pipe(
          map(() => savedId),
          catchError(() => of(savedId))
        );
      }),
      catchError(() => {
        this.toast.error('Failed to save item. Please try again.', 3000);
        return of(null);
      })
    ).subscribe(savedId => {
      if (savedId !== null) {
        this.loadItems();
        this.toast.success(`${isService ? 'Service' : 'Product'} ${isEditing ? 'updated' : 'added'} successfully`, 3000);
      }
    });
  }

  deleteItemMobile(payload: { item: EnhancedItemDTO }): void {
    this.deleteItem(payload.item);
  }

  createCategoryMobile(name: string): void {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    const newCategory: CategoryDTO = { categoryId: 0, categoryName: trimmed, createdAt: new Date() };
    this.inventoryDataService.createCategory(newCategory).subscribe({
      next: (response) => {
        this.categories.push(response);
        this.toast.success('Category created', 3000);
      },
      error: () => this.toast.error('Failed to create category.', 3000),
    });
  }
}
